// frontend/src/App.js

import React, { useEffect, Suspense } from "react";
import { BrowserRouter as Router, useNavigate } from "react-router-dom";
import CssBaseline from "@mui/material/CssBaseline";
import theme from "./theme/theme";
import { AuthProvider } from "./contexts/AuthContext";
import { AnalyticsProvider } from "./contexts/AnalyticsContext";
import { SimpleAccessibilityProvider } from "./contexts/SimpleAccessibilityContext";
import { PricingProvider } from "./contexts/PricingContext";
import PageLoadingFallback from "./components/PageLoadingFallback";
import { CookieConsentBanner } from "./components/CookieConsent";
import InstallPrompt from "./components/InstallPrompt";
import PwaAwarenessSnackbar from "./components/PwaAwarenessSnackbar";
import ScrollToTop from "./components/ScrollToTop";
import RedirectIndexHtml from "./components/RedirectIndexHtml";
import CaptureAcquisition from "./components/CaptureAcquisition";
// The route table and its preload live in appRoutes.js (#464) - this module
// renders them and holds components only.
import appRoutes, { ReleaseAuthBootOnPublicRoute } from "./appRoutes";
import { armAuthBootCap } from "./utils/authBoot";
import "./styles/simple-accessibility.css";

// Component to handle redirects from 404.html for SPA routing
function RedirectHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    const redirect = sessionStorage.getItem('redirect');
    if (redirect) {
      sessionStorage.removeItem('redirect');
      navigate(redirect);
    }
  }, [navigate]);

  return null;
}

// Cache names must match service-worker.js exactly.
const PUSH_NAV_CACHE = 'push-nav-pending-v1';
const PUSH_NAV_KEY = '/__push_pending_nav__';
const PUSH_NAV_TTL_MS = 30_000; // ignore stale entries older than 30 s

// Handles navigation messages posted by the service worker's notificationclick handler.
// Two complementary mechanisms give reliable cross-platform behaviour:
//
// 1. Cache-on-mount check (iOS fallback):
//    iOS Safari PWA often reloads the app when resuming from suspension, so the
//    postMessage is lost. The SW writes the target URL to the Cache API before
//    waking the app; this effect reads it immediately on mount and navigates.
//
// 2. postMessage listener (fast path for Android / desktop / live iOS app):
//    When the app is already running, the SW postMessage arrives instantly and
//    React Router handles the transition without a full reload. The cache entry
//    is cleared here too so the mount-check never double-navigates.
function PushNavigationHandler() {
  const navigate = useNavigate();

  // Effect 1: cache-based fallback — runs once on mount.
  useEffect(() => {
    if (!('caches' in window)) return;
    (async () => {
      try {
        const cache = await caches.open(PUSH_NAV_CACHE);
        const resp = await cache.match(new Request(PUSH_NAV_KEY));
        if (!resp) return;
        const { url, ts } = await resp.json();
        await cache.delete(new Request(PUSH_NAV_KEY));
        if (url && Date.now() - ts < PUSH_NAV_TTL_MS) {
          navigate(url);
        }
      } catch (e) {
        // Cache API unavailable or corrupt — ignore silently.
      }
    })();
  }, [navigate]);

  // Effect 2: live postMessage listener — fast path when app is already running.
  useEffect(() => {
    if (!navigator.serviceWorker) return;

    const handleMessage = (event) => {
      if (event.data?.type === 'PUSH_NAVIGATION' && event.data.url) {
        // Clear the cache entry so the mount-check won't re-navigate on next load.
        if ('caches' in window) {
          caches.open(PUSH_NAV_CACHE)
            .then((cache) => cache.delete(new Request(PUSH_NAV_KEY)))
            .catch(() => {});
        }
        navigate(event.data.url);
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => navigator.serviceWorker.removeEventListener('message', handleMessage);
  }, [navigate]);

  return null;
}

// NOTE: the Google OAuth provider from @react-oauth/google used to wrap
// everything below, above <Router>. It was moved into
// src/components/auth/GoogleAuthButton.js (backlog #119A) — that provider loads
// the Google Identity script on MOUNT, so mounting it here fetched
// accounts.google.com on the homepage, the blog and the legal pages for
// visitors who will never sign in. Do not reintroduce it here: the reasoning
// and the two guards that enforce it are documented in GoogleAuthButton.js.
// (The component's name is deliberately not spelled out — CI greps this
// directory for it, and prose naming it would trip that guard.)

function App() {
  // The authenticated-boot splash (public/index.html, backlog #158) is taken
  // down by the page itself once it has painted - AuthLayout's first commit,
  // ProtectedRoute's for a protected page without AuthLayout, ProtectedRoute's
  // redirect to /login once Login's chunk has settled (loaded or failed), or -
  // for a public page without AuthLayout - ReleaseAuthBootOnPublicRoute,
  // rendered below as the last child of the route <Suspense> so it commits
  // with the page (utils/authBoot.js). This is the BACKSTOP: at the latest 3s after
  // navigation start it comes down whatever is mounted (a page or Login chunk
  // still crawling over a slow connection).
  // Deliberately at APP level, not inside Homepage or RootRoute: the 404.html
  // shim rewrites the path right after boot (a /dashboard bookmark lands on "/"
  // and is rewritten back), so the "/" route component may never mount at all
  // - and the splash must still come down. The deadline is absolute, so the
  // StrictMode remount re-arms the same moment; a no-op (no timer at all) for
  // the anonymous boot that never added the class. The head script's 4s
  // failsafe stays the bound for a React that never mounts.
  useEffect(() => armAuthBootCap(), []);

  return (
    <Router>
      <ScrollToTop />
      <RedirectIndexHtml />
      <RedirectHandler />
      <PushNavigationHandler />
      <CaptureAcquisition />
      <AuthProvider>
        <PricingProvider>
          <AnalyticsProvider>
            <SimpleAccessibilityProvider baseTheme={theme}>
            <CssBaseline />
            <CookieConsentBanner />
            <InstallPrompt />
            <PwaAwarenessSnackbar />
            <Suspense fallback={<PageLoadingFallback />}>
            {appRoutes}
            <ReleaseAuthBootOnPublicRoute />
          </Suspense>
          </SimpleAccessibilityProvider>
          </AnalyticsProvider>
        </PricingProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
